import { v4 as uuiv4 } from 'uuid'
import {
  BACKEND_TOKEN_GENERATOR,
  JWT_ACCESS_TOKEN,
  REFRESH_TOKEN,
  USER_INFO,
  BACKEND_VERSION,
  IMPERSONATING,
  ORIGINAL_JWT_ACCESS_TOKEN,
  ORIGINAL_REFRESH_TOKEN,
  ORIGINAL_USER_INFO,
} from '@/constants/auth'
import { useAuthStore } from '@/stores/auth'
import type { LoginRequest } from '@/models/auth'
import loginQuery from '@/queries/login.gql'
import { getAdaptedUser } from '@/utils/adapters'
import { useMembershipStore } from '@/stores/membership'
import { useUserAccountStore } from '@/stores/userAccount'
import { TRIAL_EXPIRED_MODAL_KEY } from '~/constants/app'
import type { NuxtApp } from '#app'

export default defineNuxtPlugin(
  ({
    $apollo,
    $cookies,
    $token,
    $config,
    $membership,
    $hubspotIdentity,
    $rudderstack,
  }: NuxtApp) => {
    const authStore = useAuthStore()
    const membershipStore = useMembershipStore()
    const userAccountStore = useUserAccountStore()

    const login = async (params: LoginRequest) => {
      const response = await $apollo.privateClient.mutate({
        mutation: loginQuery,
        variables: {
          clientMutationId: uuiv4(),
          username: params.username,
          password: params.password,
        },
      })

      const { authToken, refreshToken, user } = response.data.login
      localStorage.setItem(BACKEND_TOKEN_GENERATOR, $config.public.backendUrl)
      const adaptedUser = getAdaptedUser(user)
      $cookies.set(JWT_ACCESS_TOKEN, authToken)
      $cookies.set(REFRESH_TOKEN, refreshToken)
      $cookies.set(USER_INFO, adaptedUser)
      $cookies.set(BACKEND_VERSION, await $token.getBackendVersion())

      authStore.setToken(authToken)
      authStore.setUserInfo(adaptedUser)

      $hubspotIdentity.identifyUser()

      await $membership.loadMembershipStatuses()
      authStore.setAuthenticated(true)

      $rudderstack.trackLogIn()
    }

    const logout = () => {
      $cookies.remove(JWT_ACCESS_TOKEN)
      $cookies.remove(REFRESH_TOKEN)
      $cookies.remove(USER_INFO)
      $cookies.remove(BACKEND_VERSION)
      $cookies.remove(TRIAL_EXPIRED_MODAL_KEY)
      $cookies.remove(IMPERSONATING)

      membershipStore.resetInfo()
      userAccountStore.clear()
      authStore.clear()
      $rudderstack.trackLogOut()
    }

    const restoreAuthState = async () => {
      const token = await $token.getToken()

      authStore.setToken(token || false)
      authStore.userInfo = $cookies.get(USER_INFO)
      authStore.setAuthenticated(!!token)
    }

    const isImpersonating = () => {
      return !!$cookies.get(IMPERSONATING)
    }

    /**
     * Used after the user impersonates a user, this method allows getting back
     * to the original session.
     */
    const restoreOriginalSession = () => {
      // Restore original cookies from the originals
      $cookies.set(JWT_ACCESS_TOKEN, $cookies.get(ORIGINAL_JWT_ACCESS_TOKEN))
      $cookies.set(REFRESH_TOKEN, $cookies.get(ORIGINAL_REFRESH_TOKEN))
      $cookies.set(USER_INFO, $cookies.get(ORIGINAL_USER_INFO))

      // Remove the original cookies
      $cookies.remove(ORIGINAL_JWT_ACCESS_TOKEN)
      $cookies.remove(ORIGINAL_REFRESH_TOKEN)
      $cookies.remove(ORIGINAL_USER_INFO)
      $cookies.remove(IMPERSONATING)
    }

    return {
      provide: {
        auth: {
          login,
          logout,
          restoreAuthState,
          store: authStore,
          membershipStore,
          isImpersonating,
          restoreOriginalSession,
        },
      },
    }
  },
)
